"use client";

import { useQuery } from "@tanstack/react-query";
import { cancelSubscription, getUserSubscriptionStatus } from "./actions";
import { Button } from "../v3/themed/Button";
import { useState } from "react";
import Modal, { ModalFooter } from "../v3/themed/Modal";
import { Prisma } from "@prisma/client";
import { toastHandler } from "../ToastHandler";

const UserSubscriptionStatus = () => {
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);

    const { data: subscription } = useQuery({
        queryKey: ["user-subscription-status"],
        queryFn: () => getUserSubscriptionStatus(),
    });
    if (!subscription) return null;

    const handleCancelSubscription = async () => {
        await toastHandler({
            loadingMessage: "Cancelling subscription...",
            successMessage: "Subscription cancelled successfully",
            errorMessage: "Failed to cancel subscription",
            operation: async () => {
                await cancelSubscription(subscription.id);
            },
        });
    };

    return (
        <>
            <Button
                variant="badge"
                onClick={() => {
                    setShowSubscriptionModal(true);
                }}
            >
                Subscription:{" "}
                <span className="rounded-full bg-v3-N-100 px-2 py-1 text-xs text-black">
                    {subscription.status}
                </span>
            </Button>
            {showSubscriptionModal && (
                <SubscriptionModal
                    isOpen={showSubscriptionModal}
                    setOpen={setShowSubscriptionModal}
                    subscription={subscription}
                    handleConfirmCancel={() => {
                        setShowCancelModal(true);
                    }}
                />
            )}
            {showCancelModal && (
                <CancelSubscriptionModal
                    isOpen={showCancelModal}
                    setOpen={setShowCancelModal}
                    handleConfirm={handleCancelSubscription}
                />
            )}
        </>
    );
};

const SubscriptionModal = ({
    isOpen,
    setOpen,
    subscription,
    handleConfirmCancel,
}: {
    isOpen: boolean;
    setOpen: (open: boolean) => void;
    subscription: Prisma.UserSubscriptionGetPayload<{
        select: {
            name: true;
            status: true;
            createdAt: true;
        };
    }>;
    handleConfirmCancel: () => void;
}) => {
    return (
        <Modal
            title={"Manage your subscription"}
            description={"See the details of your subscription below."}
            open={isOpen}
            onOpenChange={setOpen}
        >
            <div className="flex flex-col gap-4">
                <div className="flex gap-2">
                    <span className="font-medium text-v3-N300">
                        Subscription Name
                    </span>
                    <span>{subscription?.name}</span>
                </div>
                <div className="flex gap-2">
                    <span className="font-medium text-v3-N300">
                        Creation Date
                    </span>
                    <span>{subscription?.createdAt.toLocaleDateString()}</span>
                </div>
                <div className="flex gap-2">
                    <span className="font-medium text-v3-N300">Status</span>
                    <span>{subscription?.status}</span>
                </div>
            </div>
            <ModalFooter>
                <Button
                    variant="destructive"
                    onClick={() => {
                        setOpen(false);
                        handleConfirmCancel();
                    }}
                >
                    Cancel Subscription
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const CancelSubscriptionModal = ({
    isOpen,
    setOpen,
    handleConfirm,
}: {
    isOpen: boolean;
    setOpen: (open: boolean) => void;
    handleConfirm: () => void;
}) => {
    return (
        <Modal title="Cancel Subscription" open={isOpen} onOpenChange={setOpen}>
            Are you sure you want to cancel your subscription?
            <ModalFooter>
                <Button
                    variant="destructive"
                    onClick={() => {
                        setOpen(false);
                        handleConfirm();
                    }}
                >
                    Confirm Subscription Cancellation
                </Button>
            </ModalFooter>
        </Modal>
    );
};
export default UserSubscriptionStatus;
